import { Directive, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumManagementSearchKey, SearchHelper } from '@helpers/search-helper';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTimezoneService } from '@services/date-timezone.service';
import { cloneDeep } from 'lodash-es';

@Directive()
export abstract class BaseSearchFormComponent<T> implements OnInit {
  @Input() public showExcel: boolean = true;
  @Input() public showBatchExport: boolean;
  @Input() public excelDisabled: boolean = false;
  @Output() public exportAsExcel: EventEmitter<any> = new EventEmitter<any>();
  @Output() public exportBatch: EventEmitter<any> = new EventEmitter<any>();
  @Output() public search: EventEmitter<T> = new EventEmitter();
  public formGroup: UntypedFormGroup;
  public isOpen: boolean = false;

  protected abstract key: enumManagementSearchKey;
  protected abstract fieldsForSettingStartOfDay: string[];
  protected abstract fieldsForSettingEndOfDay: string[];

  protected abstract activatedRoute: ActivatedRoute;
  protected abstract router: Router;
  protected abstract dateTimezoneService: DateTimezoneService;
  protected abstract searchHelper: SearchHelper;

  public ngOnInit(): void {
    this.initForm();
  }

  public clearFilters() {
    this.formGroup.reset();
    this.onSubmit();
  }

  public onSubmit() {
    SearchHelper.setLastSearch(this.key, this.formGroup.value);
    this.updateFields();
    this.setQueryParams();
    this.search.emit(this.formGroup.value);
  }

  protected initForm() {
    const querySearch = this.querySearch;
    const hasQuerySearch: boolean = Object.keys(querySearch).length > 0;
    const lastSearch = SearchHelper.getLastSearch(this.key);
    const search = hasQuerySearch ? querySearch : lastSearch;
    if (search) {
      this.formGroup.patchValue(search);
      this.updateFields();
      if (!hasQuerySearch && lastSearch) {
        this.setQueryParams();
      }
    }
    this.search.emit(this.formGroup.value);
  }

  protected setStartOfDay(controlName: string) {
    const dateControl = this.formGroup.get(controlName);
    if (dateControl.value) {
      dateControl.patchValue(this.dateTimezoneService.startOfTheDay(dateControl.value));
    }
  }

  protected setEndOfDay(controlName: string) {
    const dateControl = this.formGroup.get(controlName);
    if (dateControl.value) {
      dateControl.patchValue(this.dateTimezoneService.endOfTheDay(dateControl.value));
    }
  }

  protected setQueryParams() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: this.searchHelper.convertFiltersToParamsForQueryString(this.formGroup.value),
    });
  }

  protected get querySearch(): { dateTimeFrom: string; dateTimeTo: string } {
    return this.searchHelper.parseSearchFromQueryString(cloneDeep(this.activatedRoute.snapshot.queryParams));
  }

  protected updateFields() {
    if (this.fieldsForSettingStartOfDay && this.fieldsForSettingStartOfDay.length) {
      this.fieldsForSettingStartOfDay.forEach((field) => {
        this.setStartOfDay(field);
      });
    }
    if (this.fieldsForSettingEndOfDay && this.fieldsForSettingEndOfDay.length) {
      this.fieldsForSettingEndOfDay.forEach((field) => {
        this.setEndOfDay(field);
      });
    }
  }
}
